import {useState} from 'react';
import {useDispatch} from 'react-redux';
import {NavLink} from 'react-router-dom';
import {styled, useTheme} from '@mui/material/styles';
import {toggleAppDrawer} from 'redux/modules/general';
import {ExpandLess, ExpandMore} from '@mui/icons-material';
import {
  Box,
  List,
  Tooltip,
  ListItem,
  Collapse,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  ListItemButton,
  Typography,
} from '@mui/material';

const CustomListItem = styled(ListItem)(({theme, sideBarOpen}) => ({
  '& .MuiListItemButton-root': {
    borderRadius: sideBarOpen ? 8 : 20,
    '&:hover': {
      color: 'text.primary',
      '& .MuiListItemIcon-root > svg': {
        transform: 'scale(1.2)',
        transition: 'all 0.4s linear',
      },
    },
  },
  '& .Mui-selected': {
    color: 'text.primary',
    backgroundColor: '#00000014 !important',
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.dialogDarkBG.main,
    }),
    '& .MuiListItemText-root .MuiListItemText-primary': {
      fontWeight: 600,
    },
    '&:hover': {
      color: 'text.primary',
      background: theme.palette.grayBorder.main,
      ...theme.applyStyles('dark', {
        background: theme.palette.midGray.main,
      }),
    },
  },
}));

const SideBarMenuItem = ({sideBarOpen, menuItem}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const {title, key, Icon, submenus, path} = menuItem || {};
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenuClick = () => {
    setOpenSubMenu(!openSubMenu);
    if (mobile) dispatch(toggleAppDrawer(false));
  };

  return (
    <>
      <CustomListItem
        key={key}
        disablePadding
        sx={{display: 'block'}}
        sideBarOpen={sideBarOpen}>
        <Tooltip title={!sideBarOpen ? title : ''} placement="right">
          {path ? (
            <ListItemButton
              exact
              to={path}
              component={NavLink}
              onClick={handleMenuClick}
              activeClassName="Mui-selected"
              className={isActive => (isActive ? 'Mui-selected' : '')}
              sx={[
                {
                  mx: 2,
                  px: 2.5,
                  height: 40,
                  minHeight: 40,
                  borderRadius: '5em',
                  position: 'relative',
                },
                sideBarOpen
                  ? {
                      justifyContent: 'initial',
                    }
                  : {
                      justifyContent: 'center',
                    },
              ]}>
              {Icon && (
                <ListItemIcon
                  sx={[
                    theme => ({
                      zIndex: 2,
                      minWidth: 0,
                      color: 'text.primary',
                      mr: sideBarOpen ? 2 : 'auto',
                      ...theme.applyStyles('dark', {
                        color: theme.palette.midGray.main,
                      }),
                    }),
                  ]}>
                  <Icon />
                </ListItemIcon>
              )}
              <ListItemText
                inset={!Icon}
                sx={[
                  theme => ({
                    zIndex: 2,
                    color: 'text.primary',
                    pl: !Icon ? '40px' : '0px',
                    opacity: sideBarOpen ? 1 : 0,
                    whiteSpace: sideBarOpen ? 'normal' : 'nowrap',
                    ...theme.applyStyles('dark', {
                      color: theme.palette.midGray.main,
                    }),
                  }),
                ]}>
                <Typography variant="label-md">{title}</Typography>
              </ListItemText>
              {submenus && sideBarOpen && (
                <>{openSubMenu ? <ExpandLess /> : <ExpandMore />}</>
              )}
            </ListItemButton>
          ) : (
            <ListItemButton
              component={Box}
              onClick={handleMenuClick}
              activeClassName="Mui-selected"
              className={isActive => (isActive ? 'Mui-selected' : '')}
              sx={[
                theme => ({
                  mx: 2,
                  px: 2.5,
                  height: 40,
                  minHeight: 40,
                  borderRadius: '5em',
                  position: 'relative',
                  color: 'text.primary',
                  justifyContent: sideBarOpen ? 'initial' : 'center',
                  ...theme.applyStyles('dark', {
                    color: theme.palette.midGray.main,
                  }),
                }),
              ]}>
              {Icon && (
                <ListItemIcon
                  sx={[
                    theme => ({
                      zIndex: 2,
                      minWidth: 0,
                      color: 'text.primary',
                      mr: sideBarOpen ? 2 : 'auto',
                      ...theme.applyStyles('dark', {
                        color: theme.palette.midGray.main,
                      }),
                    }),
                  ]}>
                  <Icon />
                </ListItemIcon>
              )}
              <ListItemText
                inset={!Icon}
                sx={[
                  {
                    zIndex: 2,
                  },
                  sideBarOpen
                    ? {
                        opacity: 1,
                        whiteSpace: 'normal',
                      }
                    : {
                        opacity: 0,
                        whiteSpace: 'nowrap',
                      },
                ]}>
                <Typography variant="label-md">{title}</Typography>
              </ListItemText>
              {submenus && sideBarOpen && (
                <>{openSubMenu ? <ExpandLess /> : <ExpandMore />}</>
              )}
            </ListItemButton>
          )}
        </Tooltip>
      </CustomListItem>
      {submenus && (
        <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {Array.isArray(submenus) &&
              submenus.map((data, index) => (
                <SideBarMenuItem
                  key={index}
                  menuItem={data}
                  sideBarOpen={sideBarOpen}
                />
              ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default SideBarMenuItem;
